import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Moon, 
  Droplet, 
  Activity, 
  Brain, 
  Smile,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';

/**
 * Health Status Cards Component
 * Displays 5 key health metrics with current status, trend, and insights
 * Pulls data from Daily Health Loop localStorage
 */

const HealthStatusCards = () => {
  const [healthData, setHealthData] = useState({
    sleep: { status: 'Good', trend: 'stable', value: '7+ hrs', insight: 'Consistent sleep pattern', color: 'blue' },
    hydration: { status: 'Improving', trend: 'up', value: '6 glasses', insight: 'Up from 4 yesterday', color: 'cyan' },
    activity: { status: 'Good', trend: 'stable', value: 'Active', insight: '5 out of 7 days', color: 'green' },
    stress: { status: 'Stable', trend: 'stable', value: 'Moderate', insight: 'Managing well', color: 'purple' },
    mood: { status: 'Positive', trend: 'up', value: 'Good', insight: 'Better than last week', color: 'amber' }
  });

  useEffect(() => {
    // Load data from Daily Health Loop localStorage
    loadHealthData();
  }, []);

  const loadHealthData = () => {
    try {
      const dailyData = localStorage.getItem('healthloop_daily_data');
      
      if (dailyData) {
        const parsed = JSON.parse(dailyData);
        const checklist = parsed.checklist || {};
        const streaks = {
          daily: parsed.dailyStreak || 0,
          hydration: parsed.hydrationStreak || 0,
          movement: parsed.movementStreak || 0
        };

        // Analyze and update health data
        const updated = {
          sleep: analyzeSleep(checklist, streaks),
          hydration: analyzeHydration(checklist, streaks),
          activity: analyzeActivity(checklist, streaks),
          stress: analyzeStress(checklist, streaks),
          mood: analyzeMood(checklist, streaks)
        };

        setHealthData(updated);
      }
    } catch (error) {
      console.error('Error loading health data:', error);
      // Keep default mock data on error
    }
  };

  // Analysis functions
  const analyzeSleep = (checklist, streaks) => {
    // If no sleep data, use neutral default
    if (checklist.sleep === undefined) {
      return {
        status: 'Track it',
        trend: 'stable',
        value: 'Not tracked',
        insight: 'Start tracking in Daily Loop',
        color: 'gray'
      };
    }

    const hasSleep = checklist.sleep === true;
    
    return {
      status: hasSleep ? 'Good' : 'Needs attention',
      trend: hasSleep ? 'stable' : 'down',
      value: hasSleep ? '7+ hrs' : 'Low',
      insight: hasSleep ? 'Well-rested today' : 'Try sleeping earlier',
      color: hasSleep ? 'blue' : 'orange'
    };
  };

  const analyzeHydration = (checklist, streaks) => {
    if (checklist.water === undefined) {
      return {
        status: 'Track it',
        trend: 'stable',
        value: 'Not tracked',
        insight: 'Track in Daily Loop',
        color: 'gray'
      };
    }

    const hasWater = checklist.water === true;
    const streak = streaks.hydration || 0;
    
    return {
      status: hasWater ? (streak >= 3 ? 'Excellent' : 'Good') : 'Low',
      trend: streak >= 3 ? 'up' : (hasWater ? 'stable' : 'down'),
      value: hasWater ? '6+ glasses' : 'Below target',
      insight: streak >= 3 ? `${streak}-day streak!` : (hasWater ? 'Hydrated today' : 'Drink more water'),
      color: hasWater ? 'cyan' : 'orange'
    };
  };

  const analyzeActivity = (checklist, streaks) => {
    if (checklist.movement === undefined) {
      return {
        status: 'Track it',
        trend: 'stable',
        value: 'Not tracked',
        insight: 'Track in Daily Loop',
        color: 'gray'
      };
    }

    const hasMoved = checklist.movement === true;
    const streak = streaks.movement || 0;
    
    return {
      status: hasMoved ? (streak >= 5 ? 'Excellent' : 'Active') : 'Inactive',
      trend: streak >= 5 ? 'up' : (hasMoved ? 'stable' : 'down'),
      value: hasMoved ? 'Moved today' : 'Sedentary',
      insight: streak >= 5 ? `${streak}-day streak!` : (hasMoved ? 'Keep moving!' : 'Take a short walk'),
      color: hasMoved ? 'green' : 'orange'
    };
  };

  const analyzeStress = (checklist, streaks) => {
    if (checklist.stress === undefined) {
      return {
        status: 'Track it',
        trend: 'stable',
        value: 'Not tracked',
        insight: 'Check in Daily Loop',
        color: 'gray'
      };
    }

    const hasChecked = checklist.stress === true;
    
    return {
      status: hasChecked ? 'Managed' : 'Check-in needed',
      trend: 'stable',
      value: hasChecked ? 'Checked' : 'Unchecked',
      insight: hasChecked ? 'Managing well' : 'Take a moment',
      color: hasChecked ? 'purple' : 'orange'
    };
  };

  const analyzeMood = (checklist, streaks) => {
    if (checklist.mood === undefined) {
      return {
        status: 'Track it',
        trend: 'stable',
        value: 'Not tracked',
        insight: 'Log in Daily Loop',
        color: 'gray'
      };
    }

    const hasLogged = checklist.mood === true;
    const allComplete = Object.values(checklist).every(v => v === true);
    
    return {
      status: allComplete ? 'Excellent' : (hasLogged ? 'Good' : 'Log it'),
      trend: allComplete ? 'up' : (hasLogged ? 'stable' : 'stable'),
      value: allComplete ? 'Positive' : (hasLogged ? 'Logged' : 'Not logged'),
      insight: allComplete ? 'All habits complete!' : (hasLogged ? 'Mood tracked' : 'How are you feeling?'),
      color: allComplete ? 'amber' : (hasLogged ? 'amber' : 'gray')
    };
  };

  // Get trend icon
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4" />;
      case 'down':
        return <TrendingDown className="h-4 w-4" />;
      default:
        return <Minus className="h-4 w-4" />;
    }
  };

  // Get trend text and color
  const getTrendDisplay = (trend, status) => {
    switch (trend) {
      case 'up':
        return { text: 'Improving', color: 'text-green-600' };
      case 'down':
        return { text: 'Lower this week', color: 'text-orange-600' };
      default:
        return { text: 'Stable', color: 'text-gray-600' };
    }
  };

  // Color schemes for cards
  const colorSchemes = {
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    cyan: 'bg-cyan-50 border-cyan-200 text-cyan-900',
    green: 'bg-green-50 border-green-200 text-green-900',
    purple: 'bg-purple-50 border-purple-200 text-purple-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    orange: 'bg-orange-50 border-orange-200 text-orange-900',
    gray: 'bg-gray-50 border-gray-200 text-gray-900'
  };

  const iconColorSchemes = {
    blue: 'text-blue-600',
    cyan: 'text-cyan-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    amber: 'text-amber-600',
    orange: 'text-orange-600',
    gray: 'text-gray-600'
  };

  const healthCards = [
    {
      id: 'sleep',
      icon: Moon,
      label: 'Sleep',
      data: healthData.sleep
    },
    {
      id: 'hydration',
      icon: Droplet,
      label: 'Hydration',
      data: healthData.hydration
    },
    {
      id: 'activity',
      icon: Activity,
      label: 'Activity',
      data: healthData.activity
    },
    {
      id: 'stress',
      icon: Brain,
      label: 'Stress',
      data: healthData.stress
    },
    {
      id: 'mood',
      icon: Smile,
      label: 'Mood',
      data: healthData.mood
    }
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Your Health Today</h2>
        <span className="text-sm text-gray-500">Last 24 hours</span>
      </div>

      {/* Health Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {healthCards.map((card) => {
          const Icon = card.icon;
          const { status, trend, value, insight, color } = card.data;
          const trendDisplay = getTrendDisplay(trend, status);

          return (
            <Card
              key={card.id}
              className={`border-2 shadow-sm hover:shadow-md transition-shadow ${colorSchemes[color]}`}
            >
              <CardContent className="pt-6 pb-4">
                <div className="space-y-3">
                  {/* Icon and Label */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className={`h-5 w-5 ${iconColorSchemes[color]}`} />
                      <span className="font-medium text-sm">{card.label}</span>
                    </div>
                    <div className={`flex items-center gap-1 ${trendDisplay.color}`}>
                      {getTrendIcon(trend)}
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <p className="text-2xl font-bold">{value}</p>
                    <p className={`text-xs mt-0.5 ${trendDisplay.color}`}>
                      {trendDisplay.text}
                    </p>
                  </div>

                  {/* Insight */}
                  <div className="pt-2 border-t border-current/10">
                    <p className="text-xs text-gray-700">
                      {insight}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Action Message */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
        <p className="text-sm text-blue-900">
          Complete your <a href="/daily-loop" className="font-semibold underline">Daily Health Loop</a> to update these cards
        </p>
      </div>
    </div>
  );
};

export default HealthStatusCards;
